import React, { useEffect, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Box, Button, DataListItem, Dialog, Illustration, toast } from '@node-space/ui-components'
import { DurationInMilliseconds } from 'constants/General'
import { useDeleteBeneficiaryMutation } from 'hooks/mutations/useBeneficiariesMutation'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { Country } from 'types/authentication/Profile'
import {
  BeneficiaryDetails,
  BeneficiaryDetailsCurrency,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
} from 'types/beneficiaries'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'

export interface DeleteBeneficiaryProps {
  beneficiaryDetails: BeneficiaryDetails
  countries?: Country[]
  onClose: () => void
}

export const DeleteBeneficiary = ({
  beneficiaryDetails,
  countries,
  onClose,
}: DeleteBeneficiaryProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { mutate, isPending } = useDeleteBeneficiaryMutation()

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.DELETE_BENEFICIARY_INITIATE, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const getBeneficiaryAccountLabels = (currency: string, transferDestination: string) => {
    if (transferDestination === BeneficiaryTransferDestination.LOCAL) {
      switch (currency) {
        case BeneficiaryDetailsCurrency.EUR:
          return {
            accountNumberLabel: t('iban'),
            bankCodeLabel: t('bic'),
          }
        case BeneficiaryDetailsCurrency.GBP:
          return {
            accountNumberLabel: t('accountNumber'),
            bankCodeLabel: t('sortCode'),
          }
        case BeneficiaryDetailsCurrency.USD:
          return {
            accountNumberLabel: t('accountNumber'),
            bankCodeLabel: t('abaRoutingNumber'),
          }
        default:
          return {
            accountNumberLabel: t('accountNumber'),
            bankCodeLabel: t('bankCode'),
          }
      }
    } else if (transferDestination === BeneficiaryTransferDestination.INTERNATIONAL) {
      switch (currency) {
        case BeneficiaryDetailsCurrency.USD:
          return {
            accountNumberLabel: t('accountNumber'),
            bankCodeLabel: t('swiftCode'),
          }
        default:
          return {
            accountNumberLabel: t('iban'),
            bankCodeLabel: t('bic'),
          }
      }
    }
  }

  const details = useMemo(() => {
    const {
      entityType,
      currency,
      firstName,
      lastName,
      businessName,
      addressCountry,
      transferDestination,
      fiat: { accountNumber, code },
    } = beneficiaryDetails

    const countryName = countries?.find(country => country?.code === addressCountry)?.name

    const accountLabels = getBeneficiaryAccountLabels(currency, transferDestination)

    return [
      {
        label: t('manageBeneficiaries.beneficiaryDescription'),
        value:
          entityType === BeneficiaryEntityType?.INDIVIDUAL
            ? `${firstName} ${lastName}`
            : businessName,
      },
      { label: accountLabels?.accountNumberLabel, value: accountNumber },
      { label: accountLabels?.bankCodeLabel, value: code },
      { label: t('manageBeneficiaries.currency'), value: currency },
      { label: t('manageBeneficiaries.countryOfRegistration'), value: countryName },
    ].filter(detail => detail.value)
  }, [beneficiaryDetails, countries])

  const handleDelete = () => {
    mutate(beneficiaryDetails.reference?.toString(), {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [reactQueryName.BENEFICIARIES] })
        toast({
          header: t('manageBeneficiaries.beneficiaryDeleted'),
          variant: 'info',
        })
        onClose()
        track.Amp.track(AmplitudeEvent.EOF_DELETE_BENEFICIARY_SUCCESS, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.CLICK,
        })
      },
      onError: () => {
        toast({
          header: t('serverError'),
          variant: 'error',
          duration: DurationInMilliseconds.TWENTY_SECONDS,
        })
        onClose()
      },
    })
  }

  return (
    <Dialog
      title={t('manageBeneficiaries.deleteBeneficiaryTitle')}
      open
      onClose={onClose}
      Illustration={<Illustration name="CloudLightning" />}
      ActionButtons={[
        <Button
          key="deleteKey"
          variant="destructive"
          testId="delete-beneficiary"
          onClick={handleDelete}
          loading={isPending}
        >
          {t('delete')}
        </Button>,
        <Button key="cancel" onClick={onClose} variant="secondary" testId="cancel">
          {t('cancel')}
        </Button>,
      ]}
    >
      <Box>
        <Box paddingT={24}>
          {details?.map(({ label, value }) => (
            <DataListItem key={label} id={label} title={label} body={value} />
          ))}
        </Box>
      </Box>
    </Dialog>
  )
}
